import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Avatar
} from '@mui/material';
import url from '../serverUrl';

const API_URL = url;

const currencyOptions = [
  'USD',
  'HUXM',
  'GHXM',
  'ARXM',
  'SKXM',
  'NOXM',
  'CLXM',
  'KRXM',
  'TYXM',
  'ASXM',
  'NZXM',
  'BZXM',
  'HKXM',
  'MXXM',
  'IDXM',
  'INXM',
  'CNXM',
  'JPXM',
  'UKXM',
  'EUXM',
  'USXM',
  'WHXM',
  'PDXM',
  'PTXM',
  'AUXM',
  'AGXM',
  'GSXM',
  'CRXM',
  'BRXM',
  'WTXM',
  'CCXM',
  'NGXM',
  'GAUXM'
];

const CreateDCNTokenForm = () => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [formData, setFormData] = useState({
    symbol: '',
    issuer: '',
    description: '',
    digital_asset_treasury: '',
    contract_reference: '',
    currency: 'USD',

    conversion_parameters: 'None',
    conversion_option: '12',

    collateral_backing: 'Digital Asset Treasury',

    dcn_amount: '',
    face_value: '',

    yield_type: 'Fixed',
    annual_yield: '',
    hourly_yield: '',

    yield_redemption: 'Quarterly',
    yield_to_maturity: '',

    type: 'PDCN',
    seniority: 'Senior Secured',

    maturity_date: '',
    early_redemption: '',
    mandatory_hold: '',

    grade_reference: '',
    exemption_reference: '',
    region_reference: '',
    sector_reference: '',
    logo: '',
    email: '',
    treasury_key: ''
  });

  const [treasuries, setTreasuries] = useState([]);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/v2/treasury`)
      .then((res) => setTreasuries(res.data || []))
      .catch(() => setTreasuries([]));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const isZeroCoupon = formData.yield_type === 'Zero-Coupon';
  const isFDCN = formData.type === 'FDCN';

  /* ===============================
     LOGO UPLOAD (same as PDCN)
  =============================== */
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileForm = new FormData();
    fileForm.append('image', file);
    fileForm.append('fileName', file.name);

    setUploading(true);
    try {
      const res = await axios.post(
        `${API_URL}/hootdex/token-logo-upload`,
        fileForm
      );

      if (res.data.status === 'ok') {
        setFormData((prev) => ({
          ...prev,
          logo: res.data.fileName
        }));
        setAlert({
          open: true,
          severity: 'success',
          message: 'Logo uploaded successfully'
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      setAlert({
        open: true,
        severity: 'error',
        message: 'Logo upload failed'
      });
    } finally {
      setUploading(false);
    }
  };

  /* ===============================
     SUBMIT HANDLER
  =============================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.symbol ||
      !formData.issuer ||
      !formData.dcn_amount ||
      !formData.face_value
    ) {
      return setAlert({
        open: true,
        severity: 'error',
        message: 'Please fill all required fields'
      });
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        symbol: formData.symbol.toUpperCase(),
        annual_yield: isZeroCoupon ? 0 : Number(formData.annual_yield || 0),
        dcn_amount: Number(formData.dcn_amount),
        face_value: Number(formData.face_value),
        logo: formData.logo ? `${API_URL}/hootdex/images/${formData.logo}` : '',
        treasury_links: formData.treasury_key
          ? [{ treasury_key: formData.treasury_key }]
          : []
      };

      await axios.post(`${API_URL}/dcn/create`, payload);

      setAlert({
        open: true,
        severity: 'success',
        message: 'DCN created successfully'
      });

      // Reset form
      setFormData({
        symbol: '',
        issuer: '',
        description: '',
        digital_asset_treasury: '',
        contract_reference: '',
        currency: 'USD',
        conversion_parameters: 'None',
        conversion_option: '12',
        collateral_backing: 'Digital Asset Treasury',
        dcn_amount: '',
        face_value: '',
        yield_type: 'Fixed',
        annual_yield: '',
        hourly_yield: '',
        yield_redemption: 'Quarterly',
        yield_to_maturity: '',
        type: 'PDCN',
        seniority: 'Senior Secured',
        treasury_key: '',
        maturity_date: '',
        early_redemption: '',
        mandatory_hold: '',
        grade_reference: '',
        exemption_reference: '',
        region_reference: '',
        sector_reference: '',
        logo: '',
        email: ''
      });
    } catch (err) {
      setAlert({
        open: true,
        severity: 'error',
        message:
          err.response?.data?.error ||
          err.response?.data?.details ||
          err.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Create DCN
      </Typography>

      {/* LOGO PREVIEW */}
      {formData.logo && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Avatar
            src={`${API_URL}/hootdex/images/${formData.logo}`}
            sx={{ width: 80, height: 80 }}
          />
        </Box>
      )}

      {/* LOGO UPLOAD */}
      <Box display="flex" justifyContent="center" mt={2} mb={3}>
        <Button variant="outlined" component="label" disabled={uploading}>
          {uploading ? 'Uploading…' : 'Upload Logo'}
          <input
            hidden
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
          />
        </Button>
      </Box>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* BASIC INFO */}
        <TextField
          fullWidth
          label="DCN Symbol *"
          name="symbol"
          value={formData.symbol}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Issuer Name *"
          name="issuer"
          value={formData.issuer}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Link Email *"
          name="email"
          value={formData.email}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Digital Asset Treasury"
          name="digital_asset_treasury"
          value={formData.digital_asset_treasury}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          select
          fullWidth
          label="Backing Treasury"
          name="treasury_key"
          value={formData.treasury_key}
          onChange={handleChange}
          margin="normal"
          helperText="The treasury proving this token's backing. Must have immutability, withdrawal_permissions, backing_freeze_status and auditability already set."
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {treasuries.map((t) => (
            <MenuItem key={t.treasury_key} value={t.treasury_key}>
              {t.treasury_key} ({t.symbol || 'unknown'} - {t.amount})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          label="Private Placement / Contract Reference"
          name="contract_reference"
          value={formData.contract_reference}
          onChange={handleChange}
          margin="normal"
        />

        {/* CURRENCY */}
        <TextField
          select
          fullWidth
          label="Currency"
          name="currency"
          value={formData.currency}
          onChange={handleChange}
          margin="normal"
        >
          {currencyOptions.map((currency) => (
            <MenuItem key={currency} value={currency}>
              {currency}
            </MenuItem>
          ))}
        </TextField>
        {/* CONVERSION */}
        <TextField
          select
          fullWidth
          label="Conversion Parameters"
          name="conversion_parameters"
          value={formData.conversion_parameters}
          onChange={handleChange}
          margin="normal"
        >
          <MenuItem value="None">None</MenuItem>
          <MenuItem value="Equity Conversion">Equity Conversion</MenuItem>
        </TextField>

        <TextField
          select
          fullWidth
          label="Conversion Option"
          name="conversion_option"
          value={formData.conversion_option}
          onChange={handleChange}
          margin="normal"
        >
          {['12', '18', '24', '36', '48'].map((m) => (
            <MenuItem key={m} value={m}>
              {m} Months
            </MenuItem>
          ))}
        </TextField>

        {/* COLLATERAL */}
        <TextField
          fullWidth
          label="Collateral Backing"
          value="Digital Asset Treasury"
          disabled
          margin="normal"
        />

        {/* AMOUNTS */}
        <TextField
          fullWidth
          label="DCN Amount *"
          name="dcn_amount"
          value={formData.dcn_amount}
          onChange={handleChange}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Face Value *"
          name="face_value"
          value={formData.face_value}
          onChange={handleChange}
          margin="normal"
        />

        {/* YIELD */}
        <TextField
          select
          fullWidth
          label="Yield Type"
          name="yield_type"
          value={formData.yield_type}
          onChange={handleChange}
          margin="normal"
        >
          <MenuItem value="Fixed">Fixed</MenuItem>
          <MenuItem value="Zero-Coupon">Zero-Coupon</MenuItem>
        </TextField>

        <TextField
          fullWidth
          label="Annual Yield (%)"
          name="annual_yield"
          value={isZeroCoupon ? '0.00' : formData.annual_yield}
          onChange={handleChange}
          disabled={isZeroCoupon}
          margin="normal"
        />

        <TextField
          select
          fullWidth
          label="Yield Token Redemption"
          name="yield_redemption"
          value={formData.yield_redemption}
          onChange={handleChange}
          margin="normal"
        >
          <MenuItem value="Quarterly">Quarterly</MenuItem>
          <MenuItem value="Semi-Annual">Semi-Annual</MenuItem>
          <MenuItem value="Annual">Annual</MenuItem>
        </TextField>

        {/* TYPE */}
        <TextField
          select
          fullWidth
          label="Type"
          name="type"
          value={formData.type}
          onChange={handleChange}
          margin="normal"
        >
          <MenuItem value="PDCN">PDCN</MenuItem>
          <MenuItem value="FDCN">FDCN</MenuItem>
        </TextField>

        {isFDCN && (
          <TextField
            fullWidth
            type="date"
            label="Maturity Date"
            name="maturity_date"
            InputLabelProps={{ shrink: true }}
            value={formData.maturity_date}
            onChange={handleChange}
            margin="normal"
          />
        )}

        <Box mt={3} textAlign="right">
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={18} />}
          >
            {loading ? 'Creating…' : 'Create DCN'}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert severity={alert.severity} variant="filled">
          {alert.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default CreateDCNTokenForm;
